import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Link,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import {useEffect, useState} from "react";
import {crud, updatePreferences} from "../crudRequests";
import {
  SMS_CONSENT_DISCLOSURE,
  SMS_CONSENT_DISCLOSURE_VERSION,
  SMS_CONSENT_SOURCE,
} from "./smsConsent";
import {
  ANNOUNCEMENT_DESCRIPTION,
  PROMOTION_DISCLOSURE,
  PROMOTION_DISCLOSURE_VERSION,
} from "./communicationConsent";

const Preferences = ({dashState}) => {
  const [email, setEmail] = useState(
    dashState?.patient?.emailPermission || false
  );
  const [text, setText] = useState(dashState?.patient?.textPermission || false);
  // Promotions need an explicit yes (absent is no); announcements are on
  // unless the patient turned them off (absent is yes).
  const [marketing, setMarketing] = useState(
    dashState?.patient?.marketingPermission === true
  );
  const [announcements, setAnnouncements] = useState(
    dashState?.patient?.announcementPermission !== false
  );

  const toast = useToast();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    crud(dashState, [
      {
        db: dashState.db,
        collection: "patients",
        parameters: [{pid: dashState?.patient?.pid}],
        method: "findOne",
      },
    ]).then((res) => {
      if (
        res?.data?.[0]?.emailPermission !== undefined &&
        res?.data?.[0]?.textPermission !== undefined
      ) {
        setEmail(res?.data?.[0]?.emailPermission);
        setText(res?.data?.[0]?.textPermission);
      }
      if (res?.data?.[0]) {
        setMarketing(res.data[0].marketingPermission === true);
        setAnnouncements(res.data[0].announcementPermission !== false);
      }
    });
  }, [dashState]);

  // const []

  return (
    <Box>
      <FormControl>
        <Flex align="center" justify="space-between">
          <Box mr={4}>
            <FormLabel
              htmlFor="email-alerts"
              mb={0}
              fontSize="sm"
              fontWeight="medium"
            >
              Email notifications
            </FormLabel>
            <Text fontSize="xs" color="gray.500" mt={1}>
              {email
                ? "Active — reminders and updates via email"
                : "Off — no email alerts"}
            </Text>
          </Box>
          <Switch
            id="email-alerts"
            isChecked={email}
            isDisabled={loading}
            colorScheme="purple"
            onChange={() => {
              setLoading(true);
              const next = !email;
              setEmail(next);
              toast
                .promise(
                  updatePreferences(
                    dashState,
                    dashState?.patient?.pid || "",
                    {email: next}
                  ),
                  {
                    success: {
                      title: "Success",
                      description: "Preference updated",
                    },
                    error: {title: "Error", description: "Something wrong"},
                    loading: {title: "Loading...", description: "Please wait"},
                  }
                )
                .catch(() => setEmail(!next))
                .finally(() => setLoading(false));
            }}
          />
        </Flex>
      </FormControl>
      <FormControl mt={4}>
        <Flex align="center" justify="space-between">
          <Box mr={4}>
            <FormLabel
              htmlFor="text-alerts"
              mb={0}
              fontSize="sm"
              fontWeight="medium"
            >
              Text messages
            </FormLabel>
            <Text fontSize="xs" color="gray.500" mt={1}>
              {text
                ? "Active — confirmations and alerts via SMS"
                : "Off — no text messages"}
            </Text>
            <Text fontSize="xs" color="gray.500" mt={2} maxW="560px">
              {SMS_CONSENT_DISCLOSURE}{" "}
              <Link
                href="https://www.brightlight.health/terms"
                isExternal
                textDecoration="underline"
              >
                Terms
              </Link>{" "}
              and{" "}
              <Link
                href="https://www.brightlight.health/privacy"
                isExternal
                textDecoration="underline"
              >
                Privacy Policy
              </Link>
              .
            </Text>
          </Box>
          <Switch
            id="text-alerts"
            isChecked={text}
            isDisabled={loading}
            colorScheme="purple"
            onChange={() => {
              setLoading(true);
              const next = !text;
              setText(next);
              toast
                .promise(
                  updatePreferences(
                    dashState,
                    dashState?.patient?.pid || "",
                    {
                      text: next,
                      textConsentSource: SMS_CONSENT_SOURCE,
                      textConsentDisclosureVersion:
                        SMS_CONSENT_DISCLOSURE_VERSION,
                    }
                  ),
                  {
                    success: {
                      title: "Success",
                      description: "Preference updated",
                    },
                    error: {title: "Error", description: "Something wrong"},
                    loading: {title: "Loading...", description: "Please wait"},
                  }
                )
                .catch(() => setText(!next))
                .finally(() => setLoading(false));
            }}
          />
        </Flex>
      </FormControl>
      <FormControl mt={4}>
        <Flex align="center" justify="space-between">
          <Box mr={4}>
            <FormLabel
              htmlFor="announcement-alerts"
              mb={0}
              fontSize="sm"
              fontWeight="medium"
            >
              Clinic announcements
            </FormLabel>
            <Text fontSize="xs" color="gray.500" mt={1}>
              {announcements
                ? "Active — important updates from your clinic via email"
                : "Off — no clinic announcements"}
            </Text>
            <Text fontSize="xs" color="gray.500" mt={2} maxW="560px">
              {ANNOUNCEMENT_DESCRIPTION}
            </Text>
          </Box>
          <Switch
            id="announcement-alerts"
            isChecked={announcements}
            isDisabled={loading}
            colorScheme="purple"
            onChange={() => {
              setLoading(true);
              const next = !announcements;
              setAnnouncements(next);
              toast
                .promise(
                  updatePreferences(
                    dashState,
                    dashState?.patient?.pid || "",
                    {announcements: next}
                  ),
                  {
                    success: {
                      title: "Success",
                      description: "Preference updated",
                    },
                    error: {title: "Error", description: "Something wrong"},
                    loading: {title: "Loading...", description: "Please wait"},
                  }
                )
                .catch(() => setAnnouncements(!next))
                .finally(() => setLoading(false));
            }}
          />
        </Flex>
      </FormControl>
      <FormControl mt={4}>
        <Flex align="center" justify="space-between">
          <Box mr={4}>
            <FormLabel
              htmlFor="marketing-alerts"
              mb={0}
              fontSize="sm"
              fontWeight="medium"
            >
              News and offers
            </FormLabel>
            <Text fontSize="xs" color="gray.500" mt={1}>
              {marketing
                ? "Active — occasional news and offers via email"
                : "Off — no news or offers"}
            </Text>
            <Text fontSize="xs" color="gray.500" mt={2} maxW="560px">
              {PROMOTION_DISCLOSURE}
            </Text>
          </Box>
          <Switch
            id="marketing-alerts"
            isChecked={marketing}
            isDisabled={loading}
            colorScheme="purple"
            onChange={() => {
              setLoading(true);
              const next = !marketing;
              setMarketing(next);
              toast
                .promise(
                  updatePreferences(
                    dashState,
                    dashState?.patient?.pid || "",
                    // The server records which wording this "yes" answered.
                    {marketing: next, marketingDisclosureVersion: PROMOTION_DISCLOSURE_VERSION}
                  ),
                  {
                    success: {
                      title: "Success",
                      description: "Preference updated",
                    },
                    error: {title: "Error", description: "Something wrong"},
                    loading: {title: "Loading...", description: "Please wait"},
                  }
                )
                .catch(() => setMarketing(!next))
                .finally(() => setLoading(false));
            }}
          />
        </Flex>
      </FormControl>
    </Box>
  );
};

export default Preferences;
